import { Menu } from "@szhsin/react-menu";
import { compact } from "lodash";
import { ArtifactType, GoalScope } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";

import {
  ExplorerFilterType,
  explorerAllowedArtifactTypes,
  explorerGithubType,
  explorerHubspotType,
  explorerJiraType,
  explorerMeetingType,
  explorerUserType,
  explorerYouTrackType,
} from "../helpers";
import ExplorerMenuButton from "./explorer-menu-button";
import ExplorerMenuOptions from "./explorer-menu-options";

const integrationSubmenu = compact([
  {
    value: explorerGithubType,
    filters: { type: explorerGithubType } as ExplorerFilterType,
    label: `Github`,
  },
  {
    value: explorerJiraType,
    filters: { type: explorerJiraType } as ExplorerFilterType,
    label: `Jira`,
  },
  {
    value: explorerHubspotType,
    filters: { type: explorerHubspotType } as ExplorerFilterType,
    label: `Hubspot`,
  },
  {
    value: explorerYouTrackType,
    filters: { type: explorerYouTrackType } as ExplorerFilterType,
    label: `YouTrack`,
  },
]);

const ExplorerTypeDropdown = ({
  filters,
  onChangeFilters,
}: {
  filters: ExplorerFilterType;
  onChangeFilters: (filters: ExplorerFilterType) => void;
}) => {
  const label = useLabel();

  const artifactTypeOptions = explorerAllowedArtifactTypes().map((value) => {
    return value === ArtifactType.Goal
      ? {
          value,
          label: label(value, { capitalize: true }),
          filters: {},
          submenu: [
            {
              value: null,
              filters: {
                type: ArtifactType.Goal,
                goalScope: null,
              } as ExplorerFilterType,
              label: `All ${label("goal")} types`,
            },
            {
              value: GoalScope.Development,
              filters: {
                type: ArtifactType.Goal,
                goalScope: GoalScope.Development,
              } as ExplorerFilterType,
              label: `${label("development", { capitalize: true })} ${label(
                "goal",
                { capitalize: true },
              )}`,
            },
            {
              value: GoalScope.Personal,
              filters: {
                type: ArtifactType.Goal,
                goalScope: GoalScope.Personal,
              } as ExplorerFilterType,
              label: `Individual ${label("goal", { capitalize: true })}`,
            },
            {
              value: GoalScope.Team,
              filters: {
                type: ArtifactType.Goal,
                goalScope: GoalScope.Team,
              } as ExplorerFilterType,
              label: `${label("team", { capitalize: true })} ${label("goal")}`,
            },
            {
              value: GoalScope.Organization,
              filters: {
                type: ArtifactType.Goal,
                goalScope: GoalScope.Organization,
              } as ExplorerFilterType,
              label: `${label("organization", {
                capitalize: true,
              })} ${label("goal")}`,
            },
          ],
        }
      : {
          value: value,
          filters: { type: value, goalScope: null } as ExplorerFilterType,
          selectedLabel: label(value, { capitalize: true }),
          label: label(value, { capitalize: true }),
        };
  });
  const typeOptions = compact([
    {
      label: "All result types",
      selectedLabel: "All result types",
      value: null,
      filters: { type: null, goalScope: null } as ExplorerFilterType,
    },
    ...artifactTypeOptions,
    {
      label: "Users",
      selectedLabel: "Users",
      value: explorerUserType,
      filters: {
        type: explorerUserType,
        goalScope: null,
      } as ExplorerFilterType,
    },
    {
      label: "Meetings",
      selectedLabel: "Meetings",
      value: explorerMeetingType,
      filters: { type: explorerMeetingType } as ExplorerFilterType,
    },
    integrationSubmenu.length && {
      value: "integration",
      label: "Integrations",
      submenu: integrationSubmenu,
      filters: {} as ExplorerFilterType,
    },
  ]);

  const handleSelectMenuItem = (updatedFilters: ExplorerFilterType) => {
    onChangeFilters(updatedFilters);
  };

  // RENDER
  return (
    <div>
      <Menu
        className="text-sm w-full z-dropdown"
        portal
        transition={false}
        menuButton={
          <ExplorerMenuButton
            options={typeOptions}
            filters={filters}
            prefix="Type"
            filterKey="type"
          />
        }
      >
        <ExplorerMenuOptions
          options={typeOptions}
          onSelectMenuItem={handleSelectMenuItem}
          filterKey="type"
        />
      </Menu>
    </div>
  );
};

export default ExplorerTypeDropdown;
